import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "searchInput",
    "searchResults",
    "staffForm",
    "existingUserInfo",
    "submitButton",
  ];

  static values = {
    searchUrl: String,
    noResultsText: String,
    noResultsHelpText: String,
    selectButtonText: String,
    foundUsersTitle: String,
    selectedUserTitle: String,
    createNewInsteadText: String,
    associateExistingButtonText: String,
    createNewButtonText: String,
  };

  connect() {
    this.timeout = null;
    this.selectedUserId = null;
    console.log("Staff search controller connected");
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  search() {
    const query = this.searchInputTarget.value.trim();

    // Clear previous timeout
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    // Debounce the search
    this.timeout = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  async performSearch(query) {
    if (query.length < 2) {
      this.hideSearchResults();
      return;
    }

    try {
      const response = await fetch(
        `${this.searchUrlValue}?search_term=${encodeURIComponent(query)}`,
      );
      const data = await response.json();

      this.displaySearchResults(data.users);
    } catch (error) {
      console.error("Search error:", error);
      this.hideSearchResults();
    }
  }

  displaySearchResults(users) {
    if (users.length === 0) {
      this.searchResultsTarget.innerHTML = `
        <section class="ds-card">
          <div class="ds-card__body">
            <div class="ds-emptyillu" style="padding:24px;">
              <span style="color:var(--ds-text-4);"><i data-lucide="user-x" style="--ds-icon-size:30px;"></i></span>
              <div class="ds-emptyillu__title">${this.noResultsTextValue}</div>
              <div class="ds-emptyillu__desc">${this.noResultsHelpTextValue}</div>
            </div>
          </div>
        </section>
      `;
      this.searchResultsTarget.classList.remove("hidden");
      window.g4sRenderIcons?.();
      return;
    }

    const resultsHtml = users
      .map((user) => {
        const initials = (user.full_name || "")
          .split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w[0])
          .join("")
          .toUpperCase();
        return `
        <div class="ds-recentrow" style="padding:14px 22px; border-top:1px solid var(--ds-border);">
          <span class="ds-avatar ds-avatar--green" style="width:38px;height:38px;font-size:14px;flex-shrink:0;">${initials}</span>
          <div class="ds-recentrow__body">
            <div class="ds-recentrow__title">${user.full_name}</div>
            <div style="display:flex; flex-direction:column; gap:2px; margin-top:3px;">
              <span class="ds-recentrow__detail" style="display:inline-flex;align-items:center;gap:6px;"><i data-lucide="mail" style="--ds-icon-size:13px;"></i>${user.email}</span>
              ${user.phonenumber ? `<span class="ds-recentrow__detail" style="display:inline-flex;align-items:center;gap:6px;"><i data-lucide="phone" style="--ds-icon-size:13px;"></i>${user.phonenumber}</span>` : ""}
              ${user.matricule ? `<span class="ds-recentrow__detail" style="display:inline-flex;align-items:center;gap:6px;"><i data-lucide="id-card" style="--ds-icon-size:13px;"></i>${user.matricule}</span>` : ""}
            </div>
          </div>
          <button
            type="button"
            class="ds-btn ds-btn--primary ds-btn--sm"
            data-action="click->staff-search#selectUser"
            data-user-id="${user.id}"
            data-user-firstname="${user.firstname || ""}"
            data-user-lastname="${user.lastname || ""}"
            data-user-email="${user.email}"
            data-user-phonenumber="${user.phonenumber || ""}"
            data-user-matricule="${user.matricule || ""}"
            data-user-data="${encodeURIComponent(JSON.stringify(user))}"
          >
            ${this.selectButtonTextValue}
          </button>
        </div>
      `;
      })
      .join("");

    this.searchResultsTarget.innerHTML = `
      <section class="ds-card">
        <div class="ds-card__header"><div class="ds-card__titles"><div class="ds-card__title">${this.foundUsersTitleValue}</div></div></div>
        <div class="ds-card__body ds-card__body--flush ds-rows">${resultsHtml}</div>
      </section>
    `;

    this.searchResultsTarget.classList.remove("hidden");
    window.g4sRenderIcons?.();
  }

  selectUser(event) {
    const button = event.currentTarget;
    const userId = button.dataset.userId;
    const firstname = button.dataset.userFirstname;
    const lastname = button.dataset.userLastname;
    const email = button.dataset.userEmail;
    const phonenumber = button.dataset.userPhonenumber;
    const matricule = button.dataset.userMatricule;

    // Récupérer toutes les données de l'utilisateur
    const userData = JSON.parse(decodeURIComponent(button.dataset.userData));

    this.selectedUserId = userId;

    this.hideSearchResults();
    this.showExistingUserInfo(
      firstname,
      lastname,
      email,
      phonenumber,
      matricule,
    );
    this.prefillFormWithUserData(userData);
    this.updateSubmitButton(true);
  }

  showExistingUserInfo(firstname, lastname, email, phonenumber, matricule) {
    this.existingUserInfoTarget.innerHTML = `
      <div class="ds-alert ds-alert--success" style="align-items:flex-start; margin-bottom:24px;">
        <i data-lucide="circle-check-big" style="--ds-icon-size:18px;"></i>
        <div>
          <div style="font-weight:700;">${this.selectedUserTitleValue}</div>
          <div style="margin-top:2px;"><strong>${(lastname || "").toUpperCase()} ${firstname || ""}</strong></div>
          <div style="display:flex; flex-direction:column; gap:2px; margin-top:3px;">
            <span style="display:inline-flex;align-items:center;gap:6px;"><i data-lucide="mail" style="--ds-icon-size:13px;"></i>${email}</span>
            ${phonenumber ? `<span style="display:inline-flex;align-items:center;gap:6px;"><i data-lucide="phone" style="--ds-icon-size:13px;"></i>${phonenumber}</span>` : ""}
            ${matricule ? `<span style="display:inline-flex;align-items:center;gap:6px;"><i data-lucide="id-card" style="--ds-icon-size:13px;"></i>${matricule}</span>` : ""}
          </div>
          <button type="button" class="ds-link" style="margin-top:8px;" data-action="click->staff-search#createNew">${this.createNewInsteadTextValue}</button>
        </div>
      </div>
      <input type="hidden" name="school_staff[existing_user_id]" value="${
        this.selectedUserId
      }">
    `;
    this.existingUserInfoTarget.classList.remove("hidden");
    window.g4sRenderIcons?.();
  }

  prefillFormWithUserData(userData) {
    // Pré-remplir les champs de base
    this.setInputValue(
      '[name="school_staff[first_name]"]',
      userData.firstname,
      true,
    );
    this.setInputValue(
      '[name="school_staff[last_name]"]',
      userData.lastname,
      true,
    );
    this.setInputValue('[name="school_staff[email]"]', userData.email, true);
    this.setInputValue(
      '[name="school_staff[phone_number]"]',
      userData.phonenumber,
    );

    // Pré-remplir les informations personnelles si disponibles
    if (userData.staff_info) {
      const staffInfo = userData.staff_info;
      this.setInputValue('[name="school_staff[gender]"]', staffInfo.gender);
      this.setInputValue(
        '[name="school_staff[birth_date]"]',
        staffInfo.birth_date,
      );
      updateDatepickerValue('[name="school_staff[birth_date]"]');
      this.setInputValue(
        '[name="school_staff[birth_place]"]',
        staffInfo.birth_place,
      );
      this.setInputValue(
        '[name="school_staff[id_card_number]"]',
        staffInfo.id_card_number,
      );
      this.setInputValue(
        '[name="school_staff[staff_type]"]',
        staffInfo.staff_type,
      );
      this.setInputValue(
        '[name="school_staff[employee_type]"]',
        staffInfo.employee_type,
      );
      this.setInputValue(
        '[name="school_staff[family_situation]"]',
        staffInfo.family_situation,
      );
      this.setInputValue(
        '[name="school_staff[qualification]"]',
        staffInfo.qualification,
      );
      this.setInputValue(
        '[name="school_staff[academic_degree]"]',
        staffInfo.academic_degree,
      );
    }

    // Pré-remplir l'adresse si disponible
    if (userData.address_info) {
      const addressInfo = userData.address_info;
      this.setInputValue(
        '[name="school_staff[address_attributes][complete_address]"]',
        addressInfo.complete_address,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][street_address]"]',
        addressInfo.street_address,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][address_line2]"]',
        addressInfo.address_line2,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][landmark]"]',
        addressInfo.landmark,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][quarter]"]',
        addressInfo.quarter,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][postal_box]"]',
        addressInfo.postal_box,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][postal_code]"]',
        addressInfo.postal_code,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][city]"]',
        addressInfo.city,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][region]"]',
        addressInfo.region,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][country]"]',
        addressInfo.country,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][country_code]"]',
        addressInfo.country_code,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][latitude]"]',
        addressInfo.latitude,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][longitude]"]',
        addressInfo.longitude,
      );
      this.setInputValue(
        '[name="school_staff[address_attributes][notes]"]',
        addressInfo.notes,
      );
    }
  }

  setInputValue(selector, value, readonly = false) {
    const input = this.staffFormTarget.querySelector(selector);
    if (!input || value === null || value === undefined || value === "") {
      return;
    }

    // Gérer les différents types d'inputs
    if (input.tagName === "SELECT") {
      console.log("SELECT", selector, value);
      // Pour les selects, trouver l'option correspondante
      const option = Array.from(input.options).find(
        (opt) => opt.value === value || opt.text === value,
      );
      if (option) {
        input.value = option.value;
        console.log("INPUT", input.value);
        console.log("OPTION", option);
        // Déclencher l'événement change pour les selects avec listeners
        // input.dispatchEvent(new Event("change", { bubbles: true }));
        // if data-slim-initialized="true" then set it to false and delete the sibling dit .ss-main and finally call initSlimSelects(selector)
        if (input.dataset.slimInitialized === "true") {
          input.dataset.slimInitialized = "false";
          // remove display none on input
          input.style.display = "";
          input.nextElementSibling.remove();
          initSlimSelects(selector);
          input.style.display = "none";
        }
      } else {
        // Debug: afficher les options disponibles si aucune correspondance
        console.log(
          `No matching option found for selector: ${selector}, value: ${value}`,
        );
        console.log(
          "Available options:",
          Array.from(input.options).map((opt) => ({
            value: opt.value,
            text: opt.text,
          })),
        );
      }
    } else if (input.type === "date") {
      // Pour les inputs date, s'assurer que le format est YYYY-MM-DD
      const dateValue = this.formatDateForInput(value);
      if (dateValue) {
        input.value = dateValue;
      }
    } else {
      // Pour les autres inputs (text, email, tel, etc.)
      input.value = value;
    }

    // Appliquer le style readonly si nécessaire
    if (readonly) {
      if (input.tagName === "SELECT") {
        input.disabled = true;
        input.classList.add("bg-gray-100");
      } else {
        input.readOnly = true;
        input.classList.add("bg-gray-100");
      }
    }
  }

  formatDateForInput(dateValue) {
    if (!dateValue) return null;

    // Si c'est déjà au format YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }

    // Essayer de parser la date
    try {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        // Formater en YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error("Error formatting date:", e);
    }

    return null;
  }

  createNew() {
    this.selectedUserId = null;
    this.hideExistingUserInfo();
    this.existingUserInfoTarget.innerHTML = "";
    this.clearForm();
    this.updateSubmitButton(false);
    this.searchInputTarget.value = "";
    this.hideSearchResults();
  }

  clearForm() {
    // Réinitialiser tous les champs du formulaire
    const inputs = this.staffFormTarget.querySelectorAll(
      'input[type="text"], input[type="email"], input[type="tel"], input[type="date"], input[type="number"], textarea, select',
    );

    inputs.forEach((input) => {
      if (input.tagName === "SELECT") {
        // Pour les selects, réinitialiser à la première option (souvent vide ou "Sélectionner...")
        input.selectedIndex = 0;
        input.disabled = false;
        input.classList.remove("bg-gray-100");
      } else {
        input.value = "";
        input.readOnly = false;
        input.classList.remove("bg-gray-100");
      }
    });
  }

  hideSearchResults() {
    this.searchResultsTarget.classList.add("hidden");
  }

  hideExistingUserInfo() {
    this.existingUserInfoTarget.classList.add("hidden");
  }

  updateSubmitButton(isExisting) {
    // Reset classes
    this.submitButtonTarget.classList.remove(
      "bg-cta",
      "hover:bg-cta-darker",
      "bg-blue-600",
      "hover:bg-blue-700",
    );

    if (isExisting) {
      this.submitButtonTarget.textContent =
        this.associateExistingButtonTextValue;
      this.submitButtonTarget.classList.add("bg-cta", "hover:bg-cta-darker");
    } else {
      this.submitButtonTarget.textContent = this.createNewButtonTextValue;
      this.submitButtonTarget.classList.add("bg-cta", "hover:bg-cta-darker");
    }
  }
}
